import {
  FocusEvent,
  KeyboardEvent,
  MouseEvent,
  SyntheticEvent,
  useEffect,
  useId,
  useMemo,
  useState
} from 'react'

import { FormControl, InputLabel, MenuItem, Select, SelectChangeEvent } from '@mui/material'
import ListSubheader from '@mui/material/ListSubheader'
import { dialogInputStopPropagation } from '@/components/ConsoleDialog'

import { ConsoleSelectSkeleton } from './components/ConsoleSelectSkeleton'
import { NoOptionsMessage } from './components/NoOptionsMessage'
import { SelectEndAdornment } from './components/SelectEndAdornment'
import {
  SELECT_BOX_DEFAULT_MAX_LIST_HEIGHT,
  SELECT_BOX_NONE_OPTION_VALUE,
  SELECT_BOX_TEST_IDS,
  SelectOpenState
} from './SelectBox.constants'
import type { SelectBoxProps } from './SelectBox.types'
import { createOptionMap, formatOption } from './SelectBox.utils'

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
export const SelectBox = <Option extends unknown>({
  defaultValue,
  value,
  getOptionId = formatOption,
  options,
  loading = false,
  testId = SELECT_BOX_TEST_IDS.root,
  label,
  clearable = false,
  disabled = false,
  disabledInfo,
  error,
  excludeSelected = false,
  name,
  noneOption = false,
  onBlur,
  onChange,
  onClear,
  onClose,
  onFocus,
  onOpen,
  noOptionsMessage = 'No options available',
  required = false,
  startAdornment,
  fullWidth = false,
  sx,
  maxListHeight = SELECT_BOX_DEFAULT_MAX_LIST_HEIGHT,
  groupBy,
  ...props
}: SelectBoxProps<Option>) => {
  const {
    formatInputOption = getOptionId,
    formatListOption = props?.formatInputOption || getOptionId
  } = props
  const [selectOpen, setSelectOpen] = useState<SelectOpenState>(SelectOpenState.Blurred)
  const [internalValue, setInternalValue] = useState<string>('')
  const inputId = useId()
  const isControlled = value !== undefined && defaultValue === undefined
  const currentValue = isControlled ? (value ? getOptionId(value) : '') : internalValue

  useEffect(() => {
    if (!isControlled && defaultValue !== undefined) {
      setInternalValue(getOptionId(defaultValue))
    }
  }, [defaultValue, isControlled])

  const optionMap = useMemo(() => createOptionMap(options, getOptionId), [options, getOptionId])
  const filteredOptions = useMemo(() => {
    if (excludeSelected && currentValue && !noneOption) {
      return options.filter((option) => getOptionId(option) !== currentValue)
    }

    if (excludeSelected && currentValue && noneOption) {
      return options.filter(
        (option) =>
          getOptionId(option) !== currentValue &&
          (typeof option === 'string' ? option !== SELECT_BOX_NONE_OPTION_VALUE : true)
      )
    }

    return options
  }, [options, excludeSelected, optionMap, currentValue])

  const groupedOptions = useMemo(() => {
    if (!groupBy) return null
    return filteredOptions.reduce(
      (groups, option) => {
        const group = groupBy(option) || 'Other'
        if (!groups[group]) groups[group] = []
        groups[group].push(option)
        return groups
      },
      {} as Record<string, Option[]>
    )
  }, [filteredOptions, groupBy])

  const hasOptions = useMemo(() => {
    if (loading) return false
    if (groupBy && groupedOptions) {
      return Object.values(groupedOptions).some((opts) => opts.length > 0)
    }
    return filteredOptions.length > 0
  }, [loading, groupBy, groupedOptions, filteredOptions])

  const shouldDisplayNoOptionsMessage = !loading && !hasOptions

  const handleChange = (e: SelectChangeEvent<string | null>) => {
    setSelectOpen(SelectOpenState.Closed)

    const selectedValue = e.target.value

    if (selectedValue === SELECT_BOX_NONE_OPTION_VALUE && noneOption) {
      setInternalValue(SELECT_BOX_NONE_OPTION_VALUE)
      onChange?.(SELECT_BOX_NONE_OPTION_VALUE as Option)
      return
    }

    if (!selectedValue && clearable) {
      setInternalValue('')
      onChange?.(null as Option)
      return
    } else if (!selectedValue) {
      return
    }

    const option = optionMap.get(selectedValue)

    if (!option) {
      return
    }

    setInternalValue(selectedValue)
    onChange?.(option)
  }

  const handleClose = (_e: SyntheticEvent<Element, Event>) => {
    setSelectOpen(SelectOpenState.Closed)
    onClose?.()
  }

  const handleOpen = (_e: SyntheticEvent<Element, Event>) => {
    setSelectOpen(SelectOpenState.Open)
    onOpen?.()
  }

  const handleFocus = (
    _e: FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>,
    openState: SelectOpenState
  ) => {
    if (openState === SelectOpenState.Blurred) {
      setSelectOpen(SelectOpenState.Closed)
    }
    onFocus?.()
  }

  const handleBlur = (_e: FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>) => {
    setSelectOpen(SelectOpenState.Blurred)
    onBlur?.()
  }

  const handleClick = (
    _e: MouseEvent<HTMLDivElement, globalThis.MouseEvent>,
    openState: SelectOpenState
  ) => {
    if (openState !== SelectOpenState.Open) {
      setSelectOpen(SelectOpenState.Open)
    }
  }

  const handleKeyDownCapture = (e: KeyboardEvent<HTMLDivElement>) => {
    e.stopPropagation()
    if (e.code === 'Tab') {
      setSelectOpen(SelectOpenState.Closed)
    }
  }

  const handleClear = () => {
    setSelectOpen(SelectOpenState.Closed)
    handleChange({ target: { value: null } } as SelectChangeEvent<string | null>)
    onClear?.()
  }

  const renderOption = (option: Option) => {
    const optionId = getOptionId(option)
    return (
      <MenuItem key={optionId} value={optionId} data-testid={SELECT_BOX_TEST_IDS.option(optionId)}>
        {formatListOption(option, { selected: optionId === currentValue })}
      </MenuItem>
    )
  }

  return (
    <>
      <FormControl
        data-testid={testId}
        fullWidth={fullWidth}
        required={required}
        disabled={disabled}
      >
        <InputLabel
          disabled={disabled}
          htmlFor={inputId}
          required={required}
          sx={{
            color: ({ palette }) => (error ? palette.error.main : undefined)
          }}
        >
          {label}
        </InputLabel>
        <Select
          {...dialogInputStopPropagation}
          sx={{
            '&.SvgIcon-root': {
              mr: 15
            },
            ...sx
          }}
          value={currentValue}
          name={name}
          required={required}
          disabled={disabled}
          error={!!error}
          label={label}
          MenuProps={{
            MenuListProps: {
              sx: {
                maxHeight: maxListHeight
              }
            },
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            ...({ 'data-testid': SELECT_BOX_TEST_IDS.optionList } as any)
          }}
          inputProps={{ id: inputId, required, 'data-testid': SELECT_BOX_TEST_IDS.input }}
          fullWidth={fullWidth}
          onOpen={handleOpen}
          onChange={handleChange}
          onClose={handleClose}
          open={disabled ? false : selectOpen === SelectOpenState.Open}
          onFocus={(e) => handleFocus(e, selectOpen)}
          onBlur={handleBlur}
          onKeyDownCapture={handleKeyDownCapture}
          onClick={(e) => handleClick(e, selectOpen)}
          renderValue={(value) => {
            if (value === SELECT_BOX_NONE_OPTION_VALUE)
              return typeof noneOption === 'string' ? noneOption : 'None'
            if (!value) return undefined

            const option = optionMap.get(value)
            if (!option) return undefined

            return formatInputOption(option)
          }}
          startAdornment={startAdornment}
          endAdornment={
            <SelectEndAdornment
              clearable={clearable}
              disabled={disabled}
              onClear={handleClear}
              hasValue={!!currentValue}
              disabledInfo={disabledInfo}
            />
          }
        >
          <ConsoleSelectSkeleton loading={loading} />

          {noneOption && (
            <MenuItem
              value={SELECT_BOX_NONE_OPTION_VALUE}
              data-testid={SELECT_BOX_TEST_IDS.noneOption}
            >
              {typeof noneOption === 'string' ? noneOption : 'None'}
            </MenuItem>
          )}

          {hasOptions &&
            (groupedOptions
              ? Object.entries(groupedOptions).map(([group, options]) => [
                  <ListSubheader key={group} data-testid={SELECT_BOX_TEST_IDS.optionGroup(group)}>
                    {group}
                  </ListSubheader>,
                  ...options.map(renderOption)
                ])
              : filteredOptions.map(renderOption))}

          <NoOptionsMessage message={noOptionsMessage} show={shouldDisplayNoOptionsMessage} />
        </Select>
      </FormControl>
      {!!error && <InputLabel error>{error}</InputLabel>}
    </>
  )
}
